import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import authService from '../services/authService';
import {
    isAuthenticated as checkIsAuthenticated,
    getRefreshToken,
    clearTokens,
    getTokenExpiryTime,
    saveTokens,
    getUserData,
    getKey,
} from '../utils/tokenStorage';
import { getMyUserProfile, updateMyUserProfile } from '../services/profileApi';

const AuthContext = createContext({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    login: async () => {},
    register: async () => {},
    logout: () => {},
    loadUser: async () => {},
    updateUser: () => {},
    updateUserVerificationStatus: () => {},
    networkError: false,
    resetNetworkError: () => {},
    sessionInitialized: false,
    markSessionAsInitialized: () => {},
    resetSessionInitialization: () => {},
    isSubscriptionRequired: false,
    setIsSubscriptionRequired: () => {},
});

export const useAuth = () => useContext(AuthContext);

const MAX_NETWORK_ERRORS_BEFORE_BACKOFF = 5;
const BASE_BACKOFF_MS = 3000;
const MAX_BACKOFF_MS = 15000;
const CUSTOM_THROTTLE_ERROR_MESSAGE = 'Too many network errors or requests. Please try again later.';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [networkError, setNetworkError] = useState(false);
    const [refreshTimerId, setRefreshTimerId] = useState(null);
    const [sessionInitialized, setSessionInitialized] = useState(false);
    const [isSubscriptionRequired, setIsSubscriptionRequired] = useState(false);

    const networkErrorCount = useRef(0);
    const consecutiveMaxErrorHits = useRef(0);
    const backoffUntil = useRef(0);

    const isCurrentlyInBackoff = useCallback(() => Date.now() < backoffUntil.current, []);

    const trackNetworkError = useCallback((operationType, error) => {
        const isGenuineNetworkError = error.message?.includes('NetworkError') ||
                              error.message?.includes('CORS') ||
                              error.name === 'TypeError' ||
                              error.message?.includes('Failed to fetch');

        if (isGenuineNetworkError) {
            networkErrorCount.current++;
            console.warn(`[AuthContext] Genuine network error during ${operationType}. Count: ${networkErrorCount.current}. Error: ${error.message}`);
            
            if (networkErrorCount.current >= MAX_NETWORK_ERRORS_BEFORE_BACKOFF && !isCurrentlyInBackoff()) {
                consecutiveMaxErrorHits.current++;
                const backoffExponent = Math.min(consecutiveMaxErrorHits.current - 1, 8);
                const backoffDuration = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, backoffExponent));
                backoffUntil.current = Date.now() + backoffDuration;
                networkErrorCount.current = 0;
                setNetworkError(true);
                setAuthError(CUSTOM_THROTTLE_ERROR_MESSAGE);
                console.error(
                    `[AuthContext] MAX_NETWORK_ERRORS reached. Backing off API requests until ${new Date(backoffUntil.current).toLocaleTimeString()}.`
                );
            }
        }
    }, [isCurrentlyInBackoff]);

    const resetNetworkError = useCallback(() => {
        networkErrorCount.current = 0;
        consecutiveMaxErrorHits.current = 0;
        backoffUntil.current = 0;
        setNetworkError(false);
    }, []);

    const wrapApiCall = useCallback(async (apiFunction, operationType) => {
        if (isCurrentlyInBackoff()) {
            const errMsg = `${CUSTOM_THROTTLE_ERROR_MESSAGE} (In backoff until ${new Date(backoffUntil.current).toLocaleTimeString()})`;
            console.warn(`[AuthContext] Operation ${operationType} throttled.`);
            setNetworkError(true);
            setAuthError(errMsg);
            throw new Error(errMsg);
        }
        try {
            const result = await apiFunction();
            resetNetworkError();
            return result;
        } catch (error) {
            trackNetworkError(operationType, error);
            throw error;
        }
    }, [isCurrentlyInBackoff, resetNetworkError, trackNetworkError]);

    const setupRefreshTimerRef = useRef(() => {});

    const updateUserStable = useCallback((newUserData) => {
        setUser(prevUser => ({ ...prevUser, ...newUserData }));
    }, []);

    const refreshTokenStable = useCallback(async () => {
        try {
            const authData = await wrapApiCall(authService.refreshToken, 'refreshToken');
            const userData = await wrapApiCall(authService.getCurrentUser, 'getCurrentUser');
            const profileData = await wrapApiCall(getMyUserProfile, 'getMyUserProfile');
            setUser({ ...authData, ...userData, ...profileData });
            setAuthError(null);
            setSessionInitialized(false);
            setupRefreshTimerRef.current();
            return { ...authData, ...userData, ...profileData };
        } catch (error) {
            // Check if it's an expected auth failure
            const isFatalAuthError = error.message?.includes('401') ||
                                     error.message?.includes('403') ||
                                     error.message?.toLowerCase().includes('unauthorized') ||
                                     error.message?.toLowerCase().includes('token refresh failed');

            // Only log unexpected errors
            if (!isFatalAuthError) {
                console.error('AuthContext: refreshTokenStable error:', error);
            }
            
            setAuthError(error.message);
            
            if (isFatalAuthError) {
                // Quietly handle logout
                clearTokens();
                setUser(null);
                setSessionInitialized(false);
            } else {
                console.warn("AuthContext: Non-fatal error during token refresh. Will retry later.");
            }
            throw error;
        }
    }, [wrapApiCall]);

    const setupRefreshTimer = useCallback(() => {
        if (networkError) {
            console.warn('AuthContext: Network error is true, skipping refresh timer setup.');
            return;
        }
        if (refreshTimerId) clearTimeout(refreshTimerId);
        
        if (!checkIsAuthenticated()) return;

        const expiryTime = getTokenExpiryTime();
        const refreshThreshold = 5 * 60 * 1000; // Refresh 5 minutes before expiry
        const msUntilExpiry = expiryTime - Date.now();
        let msUntilRefresh = Math.max(1000, msUntilExpiry - refreshThreshold);

        if (expiryTime <= 0 || msUntilExpiry <= refreshThreshold / 2) {
            // Token is expired or about to expire very soon
            // We attempt refresh immediately
            refreshTokenStable().catch(err => console.error("AuthContext: Immediate refresh in setupRefreshTimer failed:", err.message));
            return;
        }

        const newTimerId = setTimeout(() => refreshTokenStable(), msUntilRefresh);
        setRefreshTimerId(newTimerId);
    }, [networkError, refreshTimerId, refreshTokenStable]);

    useEffect(() => {
        setupRefreshTimerRef.current = setupRefreshTimer;
    }, [setupRefreshTimer]);

    const loadUserStable = useCallback(async () => {
        setIsLoading(true);
        setAuthError(null);
        try {
            // 1. Check if we have an in-memory access token (unlikely on fresh load)
            if (checkIsAuthenticated()) {
                const userData = await wrapApiCall(authService.getCurrentUser, 'getCurrentUser');
                let profileData = await wrapApiCall(getMyUserProfile, 'getMyUserProfile');
                
                if (profileData && !profileData.timezone) {
                    try {
                        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        if (browserTimezone && browserTimezone !== 'UTC') {
                            console.log(`[AuthContext] No timezone set for user. Detected: ${browserTimezone}. Updating profile...`);
                            profileData.timezone = browserTimezone;
                            updateMyUserProfile({ timezone: browserTimezone }).then(updatedProfile => {
                                updateUserStable(updatedProfile);
                                console.log('[AuthContext] User timezone automatically updated on backend.');
                            }).catch(err => {
                                console.warn('[AuthContext] Failed to automatically update user timezone on backend:', err);
                            });
                        }
                    } catch(e) {
                        console.warn('[AuthContext] Could not detect user timezone.', e);
                    }
                }

                setUser({ ...userData, ...profileData, ...user });
                setSessionInitialized(false);
                setupRefreshTimer();
            } else {
                // 2. No access token in memory. Attempt silent refresh via cookie.
                // This is the critical path for "Remember Me" functionality with HttpOnly cookies.
                if (!networkError) {
                    try {
                        await refreshTokenStable();
                        // If successful, refreshTokenStable sets the user and timer.
                    } catch (e) {
                        // Silent refresh failed (no cookie or invalid). User is logged out.
                        console.log("[AuthContext] Silent refresh failed, user is logged out.");
                        clearTokens();
                        setUser(null);
                        setSessionInitialized(false);
                    }
                } else {
                    clearTokens();
                    setUser(null);
                    setSessionInitialized(false);
                }
            }
        } catch (error) {
            console.error('AuthContext: Error during loadUserStable:', error.message);
            clearTokens();
            setUser(null);
            setSessionInitialized(false);
        } finally {
            setIsLoading(false);
        }
    }, [networkError, refreshTokenStable, setupRefreshTimer, wrapApiCall, updateUserStable, user]);

    useEffect(() => {
        loadUserStable();
        return () => {
            if (refreshTimerId) clearTimeout(refreshTimerId);
        };
    }, []);

    const loginStable = useCallback(async (usernameOrEmail, password) => {
        setIsLoading(true);
        setAuthError(null);
        setSessionInitialized(false);
        try {
            const authData = await wrapApiCall(() => authService.login(usernameOrEmail, password), 'login');
            setUser(authData);
            resetNetworkError();
            setupRefreshTimer();
            return authData;
        } catch (error) {
            setAuthError(error.message);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [wrapApiCall, resetNetworkError, setupRefreshTimer]);

    const registerStable = useCallback(async (userData) => {
        setIsLoading(true);
        setAuthError(null);
        setSessionInitialized(false);
        try {
            const authData = await wrapApiCall(() => authService.register(userData), 'register');
            saveTokens(authData);
            setUser(authData);
            resetNetworkError();
            setupRefreshTimer();
            return authData;
        } catch (error) {
            setAuthError(error.message);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [wrapApiCall, resetNetworkError, setupRefreshTimer]);

    const logoutStable = useCallback(async () => {
        await authService.logout();
        setUser(null);
        setSessionInitialized(false);
        if (refreshTimerId) clearTimeout(refreshTimerId);
        setRefreshTimerId(null);
        setNetworkError(false);
        setAuthError(null);
    }, [refreshTimerId]);

    const updateUserVerificationStatus = useCallback(() => {
        setUser(prevUser => prevUser ? { ...prevUser, verification_status: 'verified' } : null);
        const storedData = JSON.parse(localStorage.getItem(getKey('user')) || '{}');
        if (storedData) {
            storedData.verification_status = 'verified';
            localStorage.setItem(getKey('user'), JSON.stringify(storedData));
        }
    }, []);

    const contextValue = useMemo(() => ({
        isAuthenticated: !!user && !isLoading,
        user,
        isLoading,
        error: authError,
        networkError,
        login: loginStable,
        register: registerStable,
        logout: logoutStable,
        loadUser: loadUserStable,
        updateUser: updateUserStable,
        updateUserVerificationStatus,
        resetNetworkError,
        sessionInitialized,
        markSessionAsInitialized: () => setSessionInitialized(true),
        resetSessionInitialization: () => setSessionInitialized(false),
        isSubscriptionRequired,
        setIsSubscriptionRequired,
    }), [
        user, isLoading, authError, networkError, 
        loginStable, registerStable, logoutStable, loadUserStable, updateUserStable, resetNetworkError,
        sessionInitialized, isSubscriptionRequired, updateUserVerificationStatus
    ]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;